import {isFunction} from 'lodash/fp';
import {useMemo} from 'react';

import {BasicTreeViewSelectionOptions, TreeViewSelection} from 'filters/features/FilterDialog/types';

import {openFilterDialog} from 'filters';
import {useOptimizedTreeViewSelection} from 'filters/features/FilterDialog/sections/TreeViewSection/selections/useOptimizedTreeViewSelection';
import {usePolicyAgencyCodesTreeQuery} from 'modules/decisionsNext/api/trees/policyAgencyCodesTree.generated';
import {nullIfEmpty} from 'utils/arrayUtils';

import {TreeBasicNode} from 'components/Tree/TreeTypes';

import {CounterTooltip} from 'filters/components/CounterTooltip';
import {SelectedTooltip} from 'filters/features/FilterDialog/components/SelectedTooltip';
import {PolicySourcesBlock} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/PolicySourcesBlock';

export const usePolicyTagTreeViewSelection: TreeViewSelection<
  string,
  TreeBasicNode,
  {
    codes: string[] | null;
    sources?: {
      isEmpty?: boolean;
      sources: string[] | null;
    };
  },
  BasicTreeViewSelectionOptions & {
    withPolicySourceCodes?: boolean;
  }
> = (tree, {value, setValue}, {withPolicySourceCodes}) => {
  const {data} = usePolicyAgencyCodesTreeQuery({
    skip: !value.sources?.sources?.length,
  });

  const selection = useOptimizedTreeViewSelection(
    tree,
    {
      value: value.codes ?? [],
      setValue: updated =>
        setValue({
          ...value,
          codes: isFunction(updated) ? updated(value.codes ?? []) : updated,
        }),
    },
    {
      groupSelection: true,
    }
  );

  return {
    ...selection,
    selectionCount: useMemo(() => {
      const selected = selection.isAllSelected ? [] : selection.selected;
      const treeMap = tree.treeMap;

      if (!selected) {
        return;
      }

      const items = selected.map(code => treeMap.get(code)).filter(Boolean);

      return (
        <SelectedTooltip
          title={
            selected.length ? (
              <CounterTooltip
                total={`Policy tags selected: ${selected.length}`}
                groups={[
                  {
                    title: 'Category',
                    count: items.filter(item => !item.parentKey).length,
                  },
                  {
                    title: 'Tag',
                    count: items.filter(item => item.parentKey).length,
                  },
                ]}
              />
            ) : null
          }
        >
          {selected.length || 'All'}
        </SelectedTooltip>
      );
    }, [selection.isAllSelected, selection.selected, tree.treeMap]),
    additionalFilters: withPolicySourceCodes ? (
      <PolicySourcesBlock
        policySourcesFilterStore={{
          policySourcesFilter: value.sources,
        }}
        setPolicySourcesFilterStore={({policySourcesFilter}) =>
          setValue({
            ...value,
            sources:
              policySourcesFilter?.isEmpty || policySourcesFilter?.sources?.length
                ? {
                    isEmpty: policySourcesFilter.isEmpty ?? undefined,
                    sources: policySourcesFilter.sources ?? null,
                  }
                : undefined,
          })
        }
      />
    ) : null,
    overrideActiveFilterContent: ({sectionId}) => [
      withPolicySourceCodes &&
        (value.sources?.sources?.length || value.sources?.isEmpty) && {
          id: 'policy-sources',
          label: 'Policy Sources',
          chips: (value.sources?.sources || [])
            .map(source => ({
              id: source,
              label: data?.sourceCodes.policySources.find(s => s.id === source)?.name || source,
              onClick: () => openFilterDialog(sectionId),
              onDelete: () => {
                const sources = nullIfEmpty(value.sources?.sources?.filter(s => s !== source));

                return setValue({
                  ...value,
                  sources:
                    value.sources?.isEmpty || sources?.length
                      ? {
                          isEmpty: value.sources?.isEmpty,
                          sources,
                        }
                      : undefined,
                });
              },
            }))
            .concat(
              value.sources?.isEmpty
                ? [
                    {
                      id: 'No Policy Source Code',
                      label: 'No Policy Source Code',
                      onClick: () => openFilterDialog(sectionId),
                      onDelete: () =>
                        setValue({
                          ...value,
                          sources: value.sources?.sources?.length
                            ? {
                                sources: value.sources?.sources || [],
                              }
                            : undefined,
                        }),
                    },
                  ]
                : []
            ),
        },
      value.codes?.length && {
        id: 'policy-tags',
        label: 'Policy Tags',
        chips: (value.codes || []).map(code => ({
          id: code,
          label: tree.treeMap.get(code)?.title || code,
          onClick: () => openFilterDialog(sectionId),
          onDelete: () =>
            setValue({
              ...value,
              codes: nullIfEmpty((value.codes || []).filter(c => c !== code)),
            }),
        })),
      },
    ],
  };
};
